import { bearingDeg, haversineM } from './geo';
import type { CleanFix, Fix, HeadingSource } from './types';

export interface TrackOptions {
  /** Reject fixes with reported accuracy worse than this (metres). */
  maxAccuracyM: number;
  /** A car cannot move faster than this (m/s); ~140 mph. */
  maxPlausibleMps: number;
  /** After a gap longer than this (seconds), the first fix is treated as a re-acquisition and suppressed. */
  reacquireGapSec: number;
  /** How many accepted fixes to keep for deriving heading. */
  bufferSize: number;
  /** Do not trust a derived heading over a shorter baseline than this (metres). */
  minHeadingDistM: number;
  /** Trust the per-fix course only at or above this reported speed (m/s); below it the course is noise. */
  minCourseMps: number;
  /** Once stopped, a move this far from where the heading was last set gives a new one (metres, or 3× accuracy). */
  minMoveM: number;
}

const DEFAULTS: TrackOptions = {
  maxAccuracyM: 150,
  maxPlausibleMps: 62,
  reacquireGapSec: 25,
  bufferSize: 5,
  minHeadingDistM: 100,
  minCourseMps: 3,
  minMoveM: 30,
};

export type RejectReason = 'bad_accuracy' | 'non_monotonic' | 'teleport' | 'reacquire';

/**
 * Cleans raw GPS fixes: rejects garbage accuracy and teleports, suppresses the junk first
 * fix after a tunnel, and derives heading/speed from the track rather than the per-fix
 * fields (which are unreliable below ~5 m/s and -1 when stationary).
 *
 * The heading is held once known: parked at a desk, the per-fix course (0 when unknown) and
 * jitter turned the sonar every few seconds, and a wrong heading can drop a queued story as
 * "behind". It changes only for a long clean baseline, a course at driving speed, or a real
 * move away from where it was last set.
 */
export class TrackFilter {
  readonly opts: TrackOptions;
  private buf: Fix[] = [];
  private held: number | null = null;
  /** Where the heading was last set, and the bearing of the last fix past the move threshold. */
  private anchor: Fix['coords'] | null = null;
  private moving: number | null = null;
  lastReject: RejectReason | null = null;
  rejects: Record<RejectReason, number> = { bad_accuracy: 0, non_monotonic: 0, teleport: 0, reacquire: 0 };

  constructor(opts: Partial<TrackOptions> = {}) {
    this.opts = { ...DEFAULTS, ...opts };
  }

  reset(): void {
    this.buf = [];
    this.held = null;
    this.anchor = null;
    this.moving = null;
  }

  push(fix: Fix): CleanFix | null {
    const c = fix.coords;
    if (c.accuracy == null || c.accuracy > this.opts.maxAccuracyM) return this.reject('bad_accuracy');

    const prev = this.buf[this.buf.length - 1];
    let speedMps = c.speed != null && c.speed >= 0 ? c.speed : null;

    if (prev) {
      const dt = (fix.timestamp - prev.timestamp) / 1000;
      if (dt <= 0) return this.reject('non_monotonic');
      const dist = haversineM(prev.coords.latitude, prev.coords.longitude, c.latitude, c.longitude);
      if (dist / dt > this.opts.maxPlausibleMps) return this.reject('teleport');
      if (dt > this.opts.reacquireGapSec) {
        // Old points are stale for heading after a gap: restart the buffer with this fix,
        // but do not let it trigger anything.
        this.buf = [fix];
        return this.reject('reacquire');
      }
      if (speedMps == null) speedMps = dist / dt;
    }

    this.buf.push(fix);
    if (this.buf.length > this.opts.bufferSize) this.buf.shift();
    this.lastReject = null;

    const { heading, headingSource } = this.heading(c);
    return { ...fix, heading, speedMps, headingSource };
  }

  private heading(c: Fix['coords']): { heading: number | null; headingSource: HeadingSource } {
    let h: number | null = this.derivedHeading();
    let src: HeadingSource = 'derived';
    if (h == null && c.speed != null && c.speed >= this.opts.minCourseMps && c.heading != null && c.heading >= 0) {
      // The reported speed, never one worked out from jitter.
      h = c.heading;
      src = 'course';
    }
    if (h == null && this.held != null) h = this.moved(c);
    if (h == null) return { heading: this.held, headingSource: this.held == null ? null : 'held' };
    if (src !== 'course' && h === this.moving) src = 'moved';
    this.held = h;
    this.anchor = c;
    this.moving = null;
    return { heading: h, headingSource: src };
  }

  /** A bearing from the anchor once two fixes in a row are clearly away from it the same way. */
  private moved(c: Fix['coords']): number | null {
    const a = this.anchor;
    if (!a) return null;
    const d = haversineM(a.latitude, a.longitude, c.latitude, c.longitude);
    if (d < Math.max(this.opts.minMoveM, 3 * (c.accuracy ?? 0))) {
      this.moving = null;
      return null;
    }
    const b = bearingDeg(a.latitude, a.longitude, c.latitude, c.longitude);
    const prev = this.moving;
    this.moving = b;
    if (prev == null) return null;
    const diff = Math.abs(((b - prev + 540) % 360) - 180);
    return diff <= 45 ? b : null;
  }

  private reject(reason: RejectReason): null {
    this.lastReject = reason;
    this.rejects[reason]++;
    return null;
  }

  private derivedHeading(): number | null {
    if (this.buf.length < 2) return null;
    const a = this.buf[0]!.coords;
    const b = this.buf[this.buf.length - 1]!.coords;
    const d = haversineM(a.latitude, a.longitude, b.latitude, b.longitude);
    // Jitter from poor fixes can span 100 m at a desk; the baseline must also beat the error.
    if (d < this.opts.minHeadingDistM || d < 2 * ((a.accuracy ?? 0) + (b.accuracy ?? 0))) return null;
    return bearingDeg(a.latitude, a.longitude, b.latitude, b.longitude);
  }
}
